import React, { useContext, useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";

import { getBasketData, refetchBasketData } from "../queries/global";
import {
  NotificationContext,
  handleError,
} from "../context/NotificationContext";

import config from "../config/config";
import * as QUERIES from "../queries/query-functions";
import * as UTILS from "../utils/utils";

import InstanceEventSummary from "../components/instance-event-summary";
import FilterSeatsByPrice from "./filter-seats-by-price";
import FilterSeatsByArea from "./filter-seats-by-area";
import SeatingPlan from "./seating-plan";
import LoadingIndicator from "../components/loading-indicator";
import CheckForQueue from "./check-for-queue";
import CheckForPriority from "./check-for-priority";
import SeatSelectionSummary from "../book-online/seat-selection-summary";
import PromoCodeInput from "../components/promo-code-input";
import TicketStarIcon from "../svg/ticket-star";
import TransactionCommissionMessage from "../components/transaction-commission-message";

const max_tickets_bookable = config.OPTION_FIELDS.max_tickets_bookable
  ? parseInt(config.OPTION_FIELDS.max_tickets_bookable)
  : null;

const advisory_message = config.OPTION_FIELDS.booking_advisory_message;

export default function SelectSeats(props) {
  const { notification, setNotification } = useContext(NotificationContext);
  let navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();

  const { data: basketData, isFetching: basketDataFetching } = getBasketData();

  const [selectedBands, setSelectedBands] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [seatTicketIsPending, setSeatTicketIsPending] = useState(null);
  const [enabledFilters, setEnabledFilters] = useState([]);
  const [ticketGroup, setTicketGroup] = useState(null);
  const [ticketsTotal, setTicketsTotal] = useState(null);
  const [groupedPrices, setGroupedPrices] = useState([]);
  const [selectedSeatCount, setSelectedSeatCount] = useState(0);
  const [seatingPlanError, setSeatingPlanError] = useState(null);
  const [hasIsolatedSeats, setHasIsolatedSeats] = useState(false);
  const [areas, setAreas] = useState(null);
  const [protectionTicket, setProtectionTicket] = useState(null);


  const { data: planData } = useQuery({
    queryKey: ["planData", config.PLAN_ID],
    queryFn: QUERIES.getPlanData,
    staleTime: 86400000,
    refetchOnWindowFocus: false,
  });

  const { data: priceListData } = useQuery({
    queryKey: ["priceListData", config.INSTANCE_ID],
    queryFn: () => QUERIES.getInstancePriceList(config.INSTANCE_ID),
    staleTime: 86400000,
    refetchOnWindowFocus: false,
  });

  // Adding/Remove tickets
  const pendingMutationsRef = useRef(0);

  // Don't refetch basket and details data until add/remove function are completed
  const trackMutation = async (mutationFn) => {
    setSeatTicketIsPending(true);
    pendingMutationsRef.current += 1;

    try {
      await mutationFn();
    } catch (e) {
      // Error already handled in onError
    } finally {
      pendingMutationsRef.current -= 1;

      if (pendingMutationsRef.current === 0) {
        refetchBasketData();
        queryClient.invalidateQueries({
          queryKey: ["seatStatusData", config.INSTANCE_ID],
          exact: true,
        });
        setSeatTicketIsPending(false);
      }
    }
  };

  const addTicket = useMutation({
    mutationFn: ({ ticketTypeId, seatId }) =>
      QUERIES.addTickets(1, ticketTypeId, seatId),
    onSuccess: () => {      
      const protection_id = UTILS.getSupplementaryProtectionId(basketData.tickets);

      if (protection_id) {
        const protection_ticket = UTILS.getTicketsByInstance(basketData.tickets, protection_id)[0]
        QUERIES.addTickets(1, protection_ticket.ticketType.id, "", protection_ticket.instance.id, protection_ticket.instance.planId)
      }
    },
    onError: (error) => handleError(error.message, setNotification),
  });

  const removeTicket = useMutation({
    mutationFn: (seatBasketArray) => QUERIES.removeTickets(seatBasketArray),
    onSuccess: () => {
      console.log("BASKET", basketData.tickets)
    },
    onError: (error) => handleError(error.message, setNotification),
  });

  const handleAddTicket = ({ ticketTypeId, seatId }) => {
    trackMutation(() => addTicket.mutateAsync({ ticketTypeId, seatId }));
    setSelectedSeatCount((prev) => prev + 1);
  };

  const handleRemoveTicket = (seatBasketId) => {
    const seatBasketArray = [seatBasketId];

    if(protectionTicket) {
      const protection_ticket_id = protectionTicket.id;
      seatBasketArray.push(protection_ticket_id)
    }

    trackMutation(() => removeTicket.mutateAsync(seatBasketArray));
    setSelectedSeatCount((prev) => prev - 1);
  };

  useEffect(() => {
    if (!planData?.areas) return;

    const reserved_areas = planData.areas.filter(
      (area) => area.type == "Reserved"
    );

    const areas_with_bounds = reserved_areas.map((area) => {
      const xs = area.seats.map((s) => s.x);
      const ys = area.seats.map((s) => s.y);

      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      return {
        ...area,
        bounds: { minX, minY, maxX, maxY },
      };
    });

    setAreas(areas_with_bounds);
  }, [planData]);

  useEffect(() => {
    if (!basketData || !basketData?.tickets) {
      setTicketGroup(null);
      setTicketsTotal(null);
      setProtectionTicket(null)
      return;
    }

    // To show in the continue button
    let tickets = UTILS.getTicketsByInstance(basketData.tickets);
    if (!tickets?.length) {
      setTicketGroup(null);
      setTicketsTotal(null);
      setProtectionTicket(null)
      return;
    }

    setSelectedSeatCount(tickets.length);

    const grouped_tickets = UTILS.groupTicketsByInstance(tickets);
    const tickets_total = UTILS.getTicketsTotal(tickets);

    setTicketGroup(grouped_tickets[0]);
    setTicketsTotal(tickets_total.toFixed(2));

    const protection_id = UTILS.getSupplementaryProtectionId(basketData.tickets);
    if (protection_id) {
      const protection_ticket = UTILS.getTicketsByInstance(basketData.tickets, protection_id)[0]

      setProtectionTicket(protection_ticket)
    }

console.log("protection_id", protection_id)

  }, [basketData]);

  useEffect(() => {
    if (!priceListData || !enabledFilters) return;

    const grouped = UTILS.groupPriceListByAmount(priceListData, enabledFilters);
    setGroupedPrices(grouped);
  }, [priceListData, enabledFilters]);

  useEffect(() => {
    if (max_tickets_bookable && selectedSeatCount > max_tickets_bookable) {
      setSeatingPlanError(
        `You can only select a maximum of ${max_tickets_bookable} tickets for this performance.`
      );
    } else if (hasIsolatedSeats) {
      setSeatingPlanError(
        `Your current selection would leave one or more seats isolated please choose more tickets or a different selection.`
      );
    } else {
      setSeatingPlanError(null);
    }
  }, [selectedSeatCount, hasIsolatedSeats]);

  // Check to see if this event needs a queue by rendering the chooseseats iframe.
  // If the iframe goes to chooseSeats.aspx load the rest of the component
  if (!props.checkedForQueue) {
    return (
      <div className="booking-pathway__book-online">
        <LoadingIndicator type="page" />
        <CheckForQueue setCheckedForQueue={props.setCheckedForQueue} />
      </div>
    );
  }

  return (
    <div className="booking-pathway__container booking-pathway__select-seat">
      {!props.checkedForPriority && (
        <CheckForPriority setCheckedForPriority={props.setCheckedForPriority} />
      )}

      <InstanceEventSummary showAlternativeDates="true" />
      

      <section className="booking-pathway__select-seat--header">
        <div className="booking-pathway__title-row">
          <h2 className="booking-pathway__header">Where would you like to sit?</h2>
          {
            config.HAS_BEST_AVAILABLE && 
              <Link className="booking-pathway__best-seat-link" to={`/book-online/${pathname.split("/")[2]}`}><span>Find the best seats</span><TicketStarIcon/></Link>
          }
        </div>

        <div className="booking-pathway__filters">
          {enabledFilters?.length ? (
            <>
              {groupedPrices?.length ? (
                <section className="booking-pathway__filters--prices">
                  <h2 className="booking-pathway__header">Filter by price</h2>
                  <ul
                    className="booking-pathway__filter-list booking-pathway__filter-list--price"
                    role="list"
                  >
                    {groupedPrices.map((price) => (
                      <li key={price.amount}>
                        <FilterSeatsByPrice
                          price={price}
                          selectedBands={selectedBands}
                          setSelectedBands={setSelectedBands}
                          enabledFilters={enabledFilters}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
              {areas?.length && config.OPTION_FIELDS.show_location_filters ? (
                <section className="booking-pathway__filters--areas">
                  <h2 className="booking-pathway__header">
                    Filter by section
                  </h2>
                  <ul
                    className="booking-pathway__filter-list booking-pathway__filter-list--area"
                    role="list"
                  >
                    {areas.map((area) => {
                      if (enabledFilters.includes(area.id))
                        return (
                          <li key={area.id}>
                            <FilterSeatsByArea
                              area={area}
                              selectedArea={selectedArea}
                              setSelectedArea={setSelectedArea}
                              enabledFilters={enabledFilters}
                            />
                          </li>
                        );
                    })}
                  </ul>
                </section>
              ) : null}
            </>
          ) : null}
        </div>
      </section>

      <SeatingPlan
        planData={planData}
        groupedPrices={groupedPrices}
        selectedBands={selectedBands}
        selectedArea={selectedArea}
        setSeatTicketIsPending={setSeatTicketIsPending}
        seatTicketIsPending={seatTicketIsPending}
        setEnabledFilters={setEnabledFilters}
        enabledFilters={enabledFilters}
        addTicket={handleAddTicket}
        removeTicket={handleRemoveTicket}
        selectedSeatCount={selectedSeatCount}
        seatingPlanError={seatingPlanError}
        setHasIsolatedSeats={setHasIsolatedSeats}
      />

      <SeatSelectionSummary setCheckedForPriority = {props.setCheckedForPriority} checkedForPriority={props.checkedForPriority} showType = {true}/>

      <div className="booking-pathway__advisory-message">
        {  advisory_message && 
          <div className="booking-pathway__advisory-message-text"
              dangerouslySetInnerHTML={{
                          __html:advisory_message.innerHTML,
                        }}
          >          
          </div>
        }
        <TransactionCommissionMessage/>
      </div>

      

      <PromoCodeInput />        

      <div className="booking-pathway__footer">
        <button
          className="btn btn--primary"
          type="button"
          role="link"
          onClick={() => {
            navigate(props.continueLink);
          }}
          disabled={
            basketDataFetching ||
            seatTicketIsPending ||
            !ticketGroup?.tickets.length ||
            seatingPlanError
          }
        >
           Secure these tickets
          {basketDataFetching || seatTicketIsPending && (
            <LoadingIndicator />
          )}
        </button>
      </div>
    </div>
  );
}
